import { t } from "@excalidraw/excalidraw/i18n";
import { SignInButton, SignUpButton } from "@clerk/clerk-react";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

import "./LandingPage.scss";

const PenToolIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 19l7-7 3 3-7 7-3-3z" />
    <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
    <path d="M2 2l7.586 7.586" />
    <circle cx="11" cy="11" r="2" />
  </svg>
);

const VideoIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M23 7l-7 5 7 5V7z" />
    <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);

const UsersIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

export const LandingPage = () => {
  useDocumentMeta();

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-page__nav" aria-label="Main navigation">
        <div className="landing-page__nav-inner">
          <div className="landing-page__logo">
            <svg
              width="28"
              height="28"
              viewBox="0 0 109 109"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M54.5 0C24.4 0 0 24.4 0 54.5S24.4 109 54.5 109 109 84.6 109 54.5 84.6 0 54.5 0zm0 99C29.9 99 10 79.1 10 54.5S29.9 10 54.5 10 99 29.9 99 54.5 79.1 99 54.5 99z"
                fill="currentColor"
              />
              <path
                d="M54.5 20c-19 0-34.5 15.5-34.5 34.5S35.5 89 54.5 89 89 73.5 89 54.5 73.5 20 54.5 20zm0 59c-13.5 0-24.5-11-24.5-24.5S41 30 54.5 30 79 41 79 54.5 68 79 54.5 79z"
                fill="currentColor"
              />
            </svg>
            <span className="landing-page__logo-text">Excalidraw</span>
          </div>
          <div className="landing-page__nav-actions">
            <SignInButton mode="modal">
              <button className="landing-page__btn landing-page__btn--ghost">
                {t("landing.ctaSignIn")}
              </button>
            </SignInButton>
            <SignUpButton mode="modal">
              <button className="landing-page__btn landing-page__btn--primary">
                {t("landing.ctaSignUp")}
              </button>
            </SignUpButton>
          </div>
        </div>
      </nav>

      <main>
        {/* Hero Section */}
        <section className="landing-page__hero">
          <div className="landing-page__hero-content">
            <h1 className="landing-page__title">{t("landing.heroTitle")}</h1>
            <p className="landing-page__subtitle">
              {t("landing.heroSubtitle")}
            </p>
            <div className="landing-page__hero-actions">
              <SignUpButton mode="modal">
                <button className="landing-page__btn landing-page__btn--primary landing-page__btn--lg">
                  {t("landing.ctaSignUp")}
                </button>
              </SignUpButton>
              <SignInButton mode="modal">
                <button className="landing-page__btn landing-page__btn--outline landing-page__btn--lg">
                  {t("landing.ctaSignIn")}
                </button>
              </SignInButton>
            </div>
          </div>
          <div className="landing-page__hero-image">
            <img
              src="/og-image-3.png"
              alt={t("landing.heroImageAlt")}
              width="920"
              height="518"
              loading="eager"
            />
          </div>
        </section>

        {/* Features Section */}
        <section
          className="landing-page__features"
          aria-labelledby="features-heading"
        >
          <h2 id="features-heading" className="visually-hidden">
            {t("landing.featuresHeading")}
          </h2>
          <div className="landing-page__features-grid">
            <article className="landing-page__feature-card">
              <div className="landing-page__feature-icon">
                <PenToolIcon />
              </div>
              <h3 className="landing-page__feature-title">
                {t("landing.featureDrawTitle")}
              </h3>
              <p className="landing-page__feature-desc">
                {t("landing.featureDrawDescription")}
              </p>
            </article>
            <article className="landing-page__feature-card">
              <div className="landing-page__feature-icon">
                <VideoIcon />
              </div>
              <h3 className="landing-page__feature-title">
                {t("landing.featureRecordTitle")}
              </h3>
              <p className="landing-page__feature-desc">
                {t("landing.featureRecordDescription")}
              </p>
            </article>
            <article className="landing-page__feature-card">
              <div className="landing-page__feature-icon">
                <UsersIcon />
              </div>
              <h3 className="landing-page__feature-title">
                {t("landing.featureCollabTitle")}
              </h3>
              <p className="landing-page__feature-desc">
                {t("landing.featureCollabDescription")}
              </p>
            </article>
          </div>
        </section>

        {/* Footer CTA */}
        <section className="landing-page__footer-cta">
          <h2 className="landing-page__footer-cta-title">
            {t("landing.footerCta")}
          </h2>
          <SignUpButton mode="modal">
            <button className="landing-page__btn landing-page__btn--primary landing-page__btn--lg">
              {t("landing.ctaSignUp")}
            </button>
          </SignUpButton>
        </section>
      </main>

      {/* Footer */}
      <footer className="landing-page__footer">
        <p>&copy; {new Date().getFullYear()} Excalidraw Recording Studio</p>
      </footer>
    </div>
  );
};
